import {useEffect, useState} from 'react'
import {useLocation, useNavigate} from 'react-router'
import Modal from '@/customComponents/uiComponents/modal/Modal.jsx'
import ModalHeader from '@/customComponents/uiComponents/modal/ModalHeader.jsx'
import ModalFooter from '@/customComponents/uiComponents/modal/ModalFooter.jsx'
import {trackDriverPath} from '@/features/fromAirportArrival/arrivalRouting'

/**
 * "YOU HAVE A RIDE IN PROGRESS" — offered once per page load, on whatever page opened.
 *
 * ── Why it exists ────────────────────────────────────────────────────────────────
 * A passenger with a live ride is almost never on this dashboard to browse: they have
 * reloaded, or followed a link, or come back to a tab, and the thing they want is the car
 * on its way to them. The portal used to drop them wherever the URL pointed — the home
 * page, the rides list — with nothing saying a ride was under way, so the tracking screen
 * had to be found.
 *
 * ── ⚠️ IT ASKS, IT DOES NOT REDIRECT ─────────────────────────────────────────────
 * A reload that moves you somewhere you did not ask to go is indistinguishable from a bug,
 * and it would make every other page unreachable while a ride is live — a passenger who
 * opened their receipts, or settings, would be thrown out of them on every refresh. So this
 * is a question with an obvious yes, not a hand-over.
 *
 * ── ⚠️ ONCE PER PAGE LOAD, AND THE FLAG IS MODULE SCOPE ──────────────────────────
 * Not per mount and not per navigation: this sits in the layout, which survives navigation,
 * but a remount (a route that unmounts the tree, a fast refresh in development) would
 * otherwise ask again. A module variable dies with the tab, which is exactly the lifetime
 * of "this page load" — the reload the user described is what re-arms it.
 */
let askedThisLoad = false

export default function ResumeRideModal({trip}) {
    const navigate = useNavigate()
    const {pathname} = useLocation()

    /* Seeded from the module flag so a remount inside one page load opens closed. */
    const [dismissed, setDismissed] = useState(askedThisLoad)

    const tripId = trip?.id ?? null
    const destination = tripId == null ? null : trackDriverPath(tripId)

    /* ⚠️ NOT WHILE THEY ARE ALREADY THERE. The prompt is a way TO the ride; offered on the
       tracking screen itself it is a dialog over the answer it is pointing at. */
    const alreadyThere = !!destination && pathname === destination

    const open = !!destination && !dismissed && !alreadyThere

    /* ⚠️ MARKED AS ASKED FROM AN EFFECT, not while rendering. Writing a module variable
       during render is a side effect in a function that has to stay pure — React may render
       this component without ever committing it, and the question would then be spent on a
       dialog nobody saw. */
    useEffect(() => { if (open) askedThisLoad = true }, [open])

    const close = () => {
        askedThisLoad = true
        setDismissed(true)
    }

    const go = () => {
        close()
        navigate(destination)
    }

    return (
        <Modal isOpen={open} onClose={close}>
            <ModalHeader title="You have a ride in progress" onClose={close}/>

            <div className="flex flex-col gap-3 text-center">
                <p className="mb-0! text-(--text-primary)">
                    {trip?.reference
                        ? `Trip ${trip.reference} is under way.`
                        : 'One of your rides is under way.'}
                </p>
                <p className="mb-0! text-(--text-secondary)">
                    Open it to follow your driver on the map, message them, or call.
                </p>
            </div>

            <ModalFooter>
                <div className="flex flex-col gap-3">
                    <button
                        type="button"
                        onClick={go}
                        className="bg-(--text-primary) text-white text-center rounded-full w-full p-3 hover:opacity-80 cursor-pointer"
                    >
                        Track my ride
                    </button>
                    <button
                        type="button"
                        onClick={close}
                        className="text-center rounded-full w-full p-3 border border-[#CFCDCD] hover:bg-[#F3F4F6] cursor-pointer"
                    >
                        Not now
                    </button>
                </div>
            </ModalFooter>
        </Modal>
    )
}

export {ResumeRideModal}
